import { CalendarDays, FileText, Plus, Trash2, Users } from 'lucide-react';
import type { HqMonthlyMeetingDraft } from '@kop/contracts';

type Props = {
  draft: HqMonthlyMeetingDraft;
  sites: readonly { id: string; name: string }[];
  disabled: boolean;
  onChange: (draft: HqMonthlyMeetingDraft) => void;
};
export function MeetingBasicEditor({ draft, disabled, onChange }: Props) {
  const update = <K extends keyof HqMonthlyMeetingDraft>(key: K, value: HqMonthlyMeetingDraft[K]) =>
    onChange({ ...draft, [key]: value });
  return (
    <section className="hqmm-panel">
      <header>
        <h2>
          <CalendarDays size={17} />
          회의 정보
        </h2>
      </header>
      <fieldset disabled={disabled} className="hqmm-fields">
        <label className="hqmm-span-2">
          회의명
          <input
            value={draft.title}
            maxLength={300}
            onChange={(event) => update('title', event.target.value)}
          />
        </label>
        <label>
          대상 월
          <select
            value={draft.month}
            onChange={(event) => update('month', Number(event.target.value))}
          >
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index} value={index + 1}>
                {index + 1}월
              </option>
            ))}
          </select>
        </label>
        <label>
          회차
          <input
            type="number"
            min="1"
            max="9999"
            value={draft.round ?? ''}
            onChange={(event) =>
              update('round', event.target.value ? Number(event.target.value) : null)
            }
          />
        </label>
        <label>
          회의일
          <input
            type="date"
            value={draft.meetingDate ?? ''}
            onChange={(event) => update('meetingDate', event.target.value || null)}
          />
        </label>
        <label>
          시작 시간
          <input
            type="time"
            value={draft.startsAt ?? ''}
            onChange={(event) => update('startsAt', event.target.value || null)}
          />
        </label>
        <label>
          장소
          <input
            value={draft.location}
            maxLength={500}
            onChange={(event) => update('location', event.target.value)}
          />
        </label>
        <label>
          진행·담당
          <input
            value={draft.facilitator}
            maxLength={200}
            onChange={(event) => update('facilitator', event.target.value)}
          />
        </label>
      </fieldset>
    </section>
  );
}

export function MeetingMinutesEditor({ draft, sites, disabled, onChange }: Props) {
  const agenda = (id: string, patch: Partial<HqMonthlyMeetingDraft['agenda'][number]>) =>
    onChange({
      ...draft,
      agenda: draft.agenda.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    });
  return (
    <>
      <section className="hqmm-panel">
        <header>
          <h2>
            <FileText size={17} />
            회의 안건·결정사항
          </h2>
          <button
            type="button"
            disabled={disabled || draft.agenda.length >= 100}
            onClick={() =>
              onChange({
                ...draft,
                agenda: [
                  ...draft.agenda,
                  {
                    id: `agenda-${crypto.randomUUID()}`,
                    title: '',
                    notes: '',
                    decision: '',
                    owner: '',
                    dueDate: null,
                    siteId: null,
                  },
                ],
              })
            }
          >
            <Plus size={15} />
            안건 추가
          </button>
        </header>
        <div className="hqmm-agenda-list">
          {draft.agenda.length ? (
            draft.agenda.map((row, index) => (
              <article className="hqmm-agenda" key={row.id}>
                <div className="hqmm-agenda-number">{String(index + 1).padStart(2, '0')}</div>
                <fieldset disabled={disabled}>
                  <div className="hqmm-agenda-title">
                    <label>
                      안건
                      <input
                        value={row.title}
                        maxLength={500}
                        onChange={(event) => agenda(row.id, { title: event.target.value })}
                      />
                    </label>
                    <button
                      type="button"
                      className="hqmm-icon-button"
                      aria-label={`안건 ${index + 1} 삭제`}
                      onClick={() =>
                        onChange({
                          ...draft,
                          agenda: draft.agenda.filter((item) => item.id !== row.id),
                        })
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="hqmm-fields hqmm-fields--two">
                    <label>
                      논의 내용
                      <textarea
                        rows={3}
                        value={row.notes}
                        maxLength={5000}
                        onChange={(event) => agenda(row.id, { notes: event.target.value })}
                      />
                    </label>
                    <label>
                      결정사항·후속 조치
                      <textarea
                        rows={3}
                        value={row.decision}
                        maxLength={5000}
                        onChange={(event) => agenda(row.id, { decision: event.target.value })}
                      />
                    </label>
                  </div>
                  <div className="hqmm-fields hqmm-fields--three">
                    <label>
                      관련 사업장
                      <select
                        value={row.siteId ?? ''}
                        onChange={(event) => agenda(row.id, { siteId: event.target.value || null })}
                      >
                        <option value="">본사·공통</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      담당
                      <input
                        value={row.owner}
                        maxLength={300}
                        onChange={(event) => agenda(row.id, { owner: event.target.value })}
                      />
                    </label>
                    <label>
                      이행 기한
                      <input
                        type="date"
                        value={row.dueDate ?? ''}
                        onChange={(event) =>
                          agenda(row.id, { dueDate: event.target.value || null })
                        }
                      />
                    </label>
                  </div>
                </fieldset>
              </article>
            ))
          ) : (
            <p className="hqmm-empty-small">회의 안건을 추가해 주세요.</p>
          )}
        </div>
      </section>
      <section className="hqmm-panel">
        <header>
          <h2>
            <FileText size={17} />
            회의록
          </h2>
        </header>
        <div className="hqmm-panel-body">
          <label className="hqmm-field">
            회의 내용
            <textarea
              rows={9}
              value={draft.minutes}
              maxLength={50000}
              disabled={disabled}
              onChange={(event) => onChange({ ...draft, minutes: event.target.value })}
            />
          </label>
        </div>
      </section>
    </>
  );
}

export function MeetingAttendeesEditor({ draft, disabled, onChange }: Props) {
  return (
    <section className="hqmm-panel">
      <header>
        <h2>
          <Users size={17} />
          참석자 <span>{draft.attendees.length}명</span>
        </h2>
        <button
          type="button"
          disabled={disabled || draft.attendees.length >= 300}
          onClick={() =>
            onChange({
              ...draft,
              attendees: [
                ...draft.attendees,
                { id: `attendee-${crypto.randomUUID()}`, name: '', organization: '', position: '' },
              ],
            })
          }
        >
          <Plus size={15} />
          참석자 추가
        </button>
      </header>
      <div className="hqmm-attendees">
        {draft.attendees.length ? (
          draft.attendees.map((person, index) => (
            <fieldset disabled={disabled} key={person.id}>
              <span>{index + 1}</span>
              {(
                [
                  ['name', '성명'],
                  ['organization', '소속'],
                  ['position', '직책'],
                ] as const
              ).map(([key, label]) => (
                <label key={key}>
                  {label}
                  <input
                    value={person[key]}
                    maxLength={key === 'organization' ? 300 : 200}
                    onChange={(event) =>
                      onChange({
                        ...draft,
                        attendees: draft.attendees.map((row) =>
                          row.id === person.id ? { ...row, [key]: event.target.value } : row,
                        ),
                      })
                    }
                  />
                </label>
              ))}
              <button
                type="button"
                className="hqmm-icon-button"
                aria-label={`참석자 ${index + 1} 삭제`}
                onClick={() =>
                  onChange({
                    ...draft,
                    attendees: draft.attendees.filter((row) => row.id !== person.id),
                  })
                }
              >
                <Trash2 size={16} />
              </button>
            </fieldset>
          ))
        ) : (
          <p className="hqmm-empty-small">등록한 참석자가 없습니다.</p>
        )}
      </div>
    </section>
  );
}
